import { useState, useEffect, useRef } from 'react'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import { BankSelectionBottomSheet } from '../../components/BottomSheetComponents/mf-execution/withdraw/BankSelectionBottomSheet'
import { mfLogoBaseUrl } from '../../env'
import { goBack, trackEvent } from '../../utils/appBridge'
import BackButton from '../../components/ui-components/BackButton'
import { useNavigate, useSearchParams } from 'react-router-dom'
import RenderHTML from '../../components/ui-components/RenderHTML'
import { UPI_HANDLES } from '../../constants'
import { UPIAdditionBankAccount, UPISetupPayload } from '../../types/mf-execution.types'
import { getBottomPadding, getTopPadding } from '../../utils/sessionStorage'
import { EventNames } from '../../types/misc.types'
import { useClickOutside } from '../../hooks/useClickOutside'

type AddUPIPageProps = {
  cta: { text: string; onClick: (payload: UPISetupPayload) => void }
  bankAccounts: UPIAdditionBankAccount[]
  isSetupError: boolean
  trackingFund?: { fundIsin: string; fundName: string }
}

export const AddUPIPage = ({ cta, bankAccounts, isSetupError, trackingFund }: AddUPIPageProps) => {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const [selectedBank, setSelectedBank] = useState<UPIAdditionBankAccount | null>(null)
  const [upiId, setUpiId] = useState('')
  const [isValidUpiId, setIsValidUpiId] = useState<boolean | null>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [upiSuggestions, setUpiSuggestions] = useState<string[]>([])
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const inputContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [atSymbolPosition, setAtSymbolPosition] = useState<number>(0)

  useEffect(() => {
    inputRef?.current?.focus()
  }, [])

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: 'Add UPI ID',
        ...(trackingFund ?? {})
      }
    })
  }, [])

  // Set the first bank as selected by default
  useEffect(() => {
    if (bankAccounts?.length > 0 && !selectedBank) {
      setSelectedBank(bankAccounts?.[0])
    }
  }, [bankAccounts, selectedBank])

  useEffect(() => {
    setIsValidUpiId(!isSetupError)
  }, [isSetupError])

  // Close suggestions when clicking outside
  useClickOutside([suggestionsRef, inputContainerRef], () => setShowSuggestions(false), showSuggestions)

  // UPI suggestions based on common UPI handles from NPCI 3rd Party Apps
  const commonUpiHandles = UPI_HANDLES

  // UPI ID validation
  const validateUpiId = (id: string): boolean => {
    const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/
    return upiRegex.test(id)
  }

  // Calculate position of "@" symbol in pixels, constrained within container bounds
  const calculateAtSymbolPosition = (value: string) => {
    if (!inputRef?.current || !inputContainerRef?.current) return 0

    const atIndex = value.indexOf('@')
    if (atIndex === -1) return 0

    // Simple measurement using canvas
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) return 0

    const inputStyles = window.getComputedStyle(inputRef?.current)
    context.font = `${inputStyles?.fontSize || '16px'} ${inputStyles?.fontFamily || 'inherit'}`

    const textBeforeAt = value.substring(0, atIndex + 1)
    const rawPosition = context.measureText(textBeforeAt).width

    // Constrain position to keep popover within container bounds
    const containerWidth = inputContainerRef?.current?.offsetWidth || 0
    const popoverWidth = 140
    return Math.max(0, Math.min(rawPosition, containerWidth - popoverWidth))
  }

  // Handle UPI ID input change
  const handleUpiIdChange = (value: string) => {
    setUpiId(value)

    if (value.length === 0) {
      setIsValidUpiId(null)
      setShowSuggestions(false)
      setAtSymbolPosition(0)
      return
    }

    // means validation is not done yet
    if (isValidUpiId === null) {
      setIsValidUpiId(true)
    } else if (isValidUpiId === false) {
      setIsValidUpiId(validateUpiId(value))
    }

    // Show suggestions immediately when "@" is typed
    if (value.includes('@')) {
      const atPos = calculateAtSymbolPosition(value)
      setAtSymbolPosition(atPos)

      const domainPart = value.split('@')?.[1]?.toLowerCase() || ''

      // Filter suggestions based on domain part (if any)
      const filteredSuggestions = domainPart
        ? commonUpiHandles.filter((handle) => handle?.toLowerCase().includes(domainPart))
        : commonUpiHandles

      // Check if current input matches any suggestion exactly
      const isMatchingSuggestion = domainPart
        ? filteredSuggestions.some(
          (suggestion) => suggestion?.toLowerCase()?.replace('@', '') === domainPart
        )
        : false

      setUpiSuggestions(filteredSuggestions)
      setShowSuggestions(!isMatchingSuggestion && filteredSuggestions.length > 0)
    } else {
      setShowSuggestions(false)
      setAtSymbolPosition(0)
    }
  }

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: string) => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: 'Add UPI ID',
        eventId: 'MFE_ADD_UPI_SUGGESTION',
        buttonLabel: suggestion,
        ...(trackingFund ?? {})
      }
    })
    const baseId = upiId.split('@')?.[0] || ''
    setUpiId(`${baseId}${suggestion}`)
    setShowSuggestions(false)
    setIsValidUpiId(!!baseId)
  }

  // Handle bank selection
  const handleBankSelect = (bank: UPIAdditionBankAccount) => {
    setSelectedBank(bank)
    closeBottomSheet()
  }

  // Open bank selection bottom sheet
  const openBankSelection = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: 'Add UPI ID',
        eventId: 'MFE_ADD_UPI_SELECT_BANK',
        buttonLabel: 'Select Bank',
        ...(trackingFund ?? {})
      }
    })
    openBottomSheet(
      <BankSelectionBottomSheet
        onDismiss={closeBottomSheet}
        onBankSelect={handleBankSelect}
        selectedBank={selectedBank}
        availableBanks={bankAccounts}
        ScreenName='AddUPIPage'
        trackingFund={trackingFund}
      />
    )
  }

  // Check if pay button should be enabled
  const isPayButtonEnabled = selectedBank && upiId && isValidUpiId === true

  const handleBack = () => {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'Add UPI ID',
        buttonLabel: 'Back',
        eventId: 'MFE_ADD_UPI_BACK',
        ...(trackingFund ?? {})
      }
    })
    if (searchParams?.get('from') === 'APP') goBack()
    else navigate(-1)
  }

  return (
    <div className='flex flex-col h-screen bg-white'>
      {/* Header */}
      <div
        style={{ paddingTop: `calc(${getTopPadding()}px + 8px)` }}
        className='flex items-center justify-between px-5 pb-4'
      >
        <div className='flex items-center justify-between w-full'>
          <BackButton size={24} onClick={handleBack} />
          <div className='text-[16px] font-[500] text-[#232323]'>Add UPI ID</div>
          <div className='w-9'></div>
        </div>
      </div>

      {/* Content */}
      <div className='flex flex-col flex-1 px-5 py-6'>
        {/* Bank Selection */}
        <div className='pb-3'>
          <div className='border border-[#F0F4F7] rounded-[12px] pl-4 pr-5 py-[10px]'>
            <div className='flex '>
              <div className='flex flex-col w-1 flex-1'>
                <div className='text-[13px] font-[400] text-[#8E9393] pb-[2px]'>Select Bank</div>
                <div
                  onClick={openBankSelection}
                  className='flex items-center justify-between w-full bg-white cursor-pointer'
                >
                  <div className='flex items-center w-full'>
                    {selectedBank ? (
                      <>
                        <img
                          src={`${mfLogoBaseUrl}${selectedBank?.logoUrl}`}
                          alt={selectedBank?.displayAccNumber}
                          className='w-4 h-4 mr-2'
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                        <span className='text-[14px] font-[500] text-[#232323] truncate max-w-[90%]'>
                          {selectedBank?.displayAccNumber}
                        </span>
                      </>
                    ) : (
                      <span className='text-[16px] text-[#6A6E6E]'>Select Bank</span>
                    )}
                  </div>
                </div>
              </div>
              <div className='pt-[18px]'>
                <RenderHTML innerHTMLText={DownCaratIcon} />
              </div>
            </div>
          </div>
        </div>

        {/* UPI ID Input */}
        <div className='pb-1'>
          <div
            className={`flex flex-col border  ${isValidUpiId === false ? ' border-[#E53E3E]' : 'border-[#F0F4F7]'} rounded-[12px] pl-4 pr-5 py-[10px]`}
          >
            <div className='text-[13px] text-[#8E9393] pb-1'>Enter UPI ID (linked to above bank)</div>
            <div className='relative' ref={inputContainerRef}>
              <input
                ref={inputRef}
                name='upiId'
                autoComplete='off'
                type='text'
                autoFocus
                value={upiId}
                onChange={(e) => handleUpiIdChange(e.target.value)}
                placeholder=''
                className={`w-full border-none text-[16px] bg-white focus:outline-none `}
                style={{
                  caretColor: '#2E8AE5'
                }}
              />

              {/* Suggestions Dropdown */}
              {showSuggestions && upiSuggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className='absolute top-full z-10 bg-white border border-[#F0F4F7] rounded-[12px] mt-1 max-h-48 overflow-y-auto w-[140px] py-[5px]'
                  style={{
                    left: `${atSymbolPosition}px`
                  }}
                >
                  {upiSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleSuggestionSelect(suggestion)}
                      className='px-4 py-[10px] hover:bg-[#F7FAFC] cursor-pointer text-[14px] text-[#6A6E6E] font-[400] last:border-b-0'
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Error Message */}
            {isValidUpiId === false && <p className='text-[12px] text-[#E53E3E] mt-2'>Invalid UPI ID</p>}
          </div>
        </div>
        <p className='text-[13px] text-[#8E9393] mt-2'>Only pay from the selected bank account to avoid order cancellation.</p>
      </div>

      {/* Pay Button */}
      <div style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }} className='px-5'>
        <button
          onClick={() => {
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: 'Add UPI ID',
                buttonLabel: cta.text,
                eventId: 'MFE_ADD_UPI_PAY',
                ...(trackingFund ?? {})
              }
            })
            // Validate UPI ID
            const isValid = validateUpiId(upiId)
            setIsValidUpiId(isValid)
            if (!isValid) return
            cta.onClick({
              accountNumber: selectedBank?.accountNumber ?? '',
              ifscCode: selectedBank?.ifscCode ?? '',
              upiId: upiId
            })
          }}
          disabled={!isPayButtonEnabled}
          className={`w-full py-4 rounded-[12px] text-[16px] font-[500] transition-colors ${isPayButtonEnabled
            ? 'bg-[#232323] text-white hover:bg-[#000000]'
            : 'bg-[#232323] opacity-25 text-[#9E9E9E] cursor-not-allowed'
            }`}
        >
          {cta.text}
        </button>
      </div>
    </div>
  )
}

export const DownCaratIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none">
  <path d="M1 1.09558L6.67365 6.76918C6.85389 6.94946 7.14611 6.94946 7.32635 6.76918L13 1.09558" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
